import { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckIcon from '@mui/icons-material/Check'
import useTranslate from '@/hooks/use-translate'
import { getNf, nf } from '../nfTokens'
import { NfSectionHeading } from '../primitives/NfSectionHeading'

/** Violet de la maquette TEC-1653 (mode préparation intelligente). */
const intelligentViolet = {
  main: '#7C3AED',
  light: '#A78BFA',
  tint: alpha('#7C3AED', 0.1),
  gradient: `linear-gradient(135deg, #A78BFA, #7C3AED)`,
} as const

type ModeVariant = 'intelligent' | 'standard'

const primaryGradient = (theme: Theme) =>
  `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`

type ModeAccent = {
  border: string
  surface: string
  title: string
  body: string
  iconSurface: string
  iconColor: string
  checkGradient: string
  checkIconColor: string
}

const getModeAccent = (theme: Theme, variant: ModeVariant, selected: boolean): ModeAccent => {
  const t = getNf(theme)
  if (!selected) {
    return {
      border: t.line,
      surface: t.surfaceMuted,
      title: t.muted2,
      body: t.muted,
      iconSurface: t.surface,
      iconColor: t.muted2,
      checkGradient: primaryGradient(theme),
      checkIconColor: theme.palette.primary.contrastText,
    }
  }

  if (variant === 'standard') {
    const main = theme.palette.primary.main
    return {
      border: main,
      surface: alpha(main, 0.08),
      title: main,
      body: main,
      iconSurface: alpha(main, 0.12),
      iconColor: main,
      checkGradient: primaryGradient(theme),
      checkIconColor: theme.palette.primary.contrastText,
    }
  }

  return {
    border: intelligentViolet.main,
    surface: intelligentViolet.tint,
    title: intelligentViolet.main,
    body: intelligentViolet.main,
    iconSurface: intelligentViolet.tint,
    iconColor: intelligentViolet.main,
    checkGradient: intelligentViolet.gradient,
    checkIconColor: '#fff',
  }
}

const ModeIconTile: FC<{ accent: ModeAccent; children: ReactNode }> = ({ accent, children }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: `${nf.radiusCard}px`,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      bgcolor: accent.iconSurface,
      color: accent.iconColor,
    }}
  >
    {children}
  </Box>
)

const ModeSelectionMark: FC<{ selected: boolean; accent: ModeAccent }> = ({ selected, accent }) => {
  if (selected) {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: accent.checkGradient,
          color: accent.checkIconColor,
        }}
      >
        <CheckIcon sx={{ fontSize: 12 }} />
      </Box>
    )
  }

  return (
    <Box
      sx={(theme) => ({
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `1.5px solid ${getNf(theme).line}`,
        bgcolor: 'transparent',
      })}
    />
  )
}

const ModeNewBadge: FC<{ label: string }> = ({ label }) => (
  <Box
    component="span"
    sx={{
      position: 'absolute',
      top: 10,
      right: 10,
      zIndex: 1,
      px: 0.875,
      py: 0.375,
      borderRadius: `${nf.radiusPill}px`,
      background: intelligentViolet.gradient,
      boxShadow: `0 1px 2px ${alpha(intelligentViolet.main, 0.25)}`,
    }}
  >
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: 9,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#fff',
      }}
    >
      {label}
    </Typography>
  </Box>
)

type ModeCardProps = {
  variant: ModeVariant
  selected: boolean
  title: string
  description: string
  icon: ReactNode
  newBadge?: string
  testId: string
  onSelect: () => void
}

const ModeCard: FC<ModeCardProps> = ({
  variant,
  selected,
  title,
  description,
  icon,
  newBadge,
  testId,
  onSelect,
}) => {
  const theme = useTheme()
  const accent = getModeAccent(theme, variant, selected)

  return (
    <ButtonBase
      type="button"
      onClick={onSelect}
      data-testid={testId}
      aria-pressed={selected}
      sx={{
        position: 'relative',
        flexGrow: selected ? 1.7 : 1,
        flexBasis: 0,
        minWidth: 0,
        display: 'block',
        textAlign: 'center',
        p: '14px 11px 12px',
        borderRadius: `${nf.radiusCard}px`,
        border: `1px solid ${accent.border}`,
        bgcolor: accent.surface,
        transition:
          'flex-grow .22s ease, border-color .15s ease, background-color .15s ease',
        alignSelf: 'stretch',
      }}
    >
      {newBadge && <ModeNewBadge label={newBadge} />}
      <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <ModeIconTile accent={accent}>{icon}</ModeIconTile>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            lineHeight: 1.3,
            color: accent.title,
            textAlign: 'center',
            px: newBadge ? 2 : 0,
            minHeight: '2.6em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            height: selected ? 56 : 0,
            opacity: selected ? 1 : 0,
            overflow: 'hidden',
            transition: 'opacity .22s ease',
            '&&': { mt: selected ? 1 : 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              lineHeight: 1.4,
              color: accent.body,
              textAlign: 'center',
            }}
          >
            {description}
          </Typography>
        </Box>
        <Box sx={{ pt: 0.5 }}>
          <ModeSelectionMark selected={selected} accent={accent} />
        </Box>
      </Stack>
    </ButtonBase>
  )
}

export type PreparationModeFieldProps = {
  agentic: boolean
  onChange: (agentic: boolean) => void
}

export const PreparationModeField: FC<PreparationModeFieldProps> = ({ agentic, onChange }) => {
  const { t: tUpload } = useTranslate('upload')

  return (
    <Stack spacing={1.25} data-testid="wizard-preparation-section">
      <NfSectionHeading>{tUpload('wizardPreparationSectionTitle')}</NfSectionHeading>
      <Stack direction="row" spacing={1} role="radiogroup" aria-label={tUpload('wizardPreparationSectionTitle')}>
        <ModeCard
          variant="intelligent"
          selected={agentic}
          title={tUpload('wizardPreparationModeIntelligentTitle')}
          description={tUpload('wizardPreparationModeIntelligentDescription')}
          icon={<AutoAwesomeIcon sx={{ fontSize: 17 }} />}
          newBadge={tUpload('wizardPreparationModeNewBadge')}
          testId="preparation-mode-intelligent"
          onSelect={() => onChange(true)}
        />
        <ModeCard
          variant="standard"
          selected={!agentic}
          title={tUpload('wizardPreparationModeStandardTitle')}
          description={tUpload('wizardPreparationModeStandardDescription')}
          icon={<LayersOutlinedIcon sx={{ fontSize: 17 }} />}
          testId="preparation-mode-standard"
          onSelect={() => onChange(false)}
        />
      </Stack>
    </Stack>
  )
}

export default PreparationModeField
